import { Slot } from "@radix-ui/react-slot";
import { ComponentPropsWithoutRef, use, useRef } from "react";
import { byShortcut } from "../../helpers/predicate";
import { useKeyboard } from "../../hooks/useKeyboard";
import { Button } from "../Button/Button";
import { TooltipContext } from "./contexts/TooltipContext";
import styles from "./Tooltip.module.css";

type TooltipTriggerProps = (
  | ({ asChild: true } & Omit<ComponentPropsWithoutRef<typeof Slot>, "color">)
  | ({
      asChild?: false;
    } & ComponentPropsWithoutRef<typeof Button>)
) & {
  delay?: number;
};

export function TooltipTrigger({
  asChild = false,
  delay = 200,
  ...props
}: TooltipTriggerProps) {
  const timeoutRef = useRef<number | null>(null);
  const { disabled, open, setOpen, tooltipId, triggerRef } =
    use(TooltipContext);
  const Component = asChild ? Slot : Button;

  useKeyboard(byShortcut("Escape"), () => setOpen(false), {
    cleanup: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
  });

  return (
    <Component
      aria-describedby={open ? tooltipId : undefined}
      className={styles.TooltipTrigger}
      onBlur={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onMouseEnter={() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => setOpen(true), delay);
      }}
      onMouseLeave={() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setOpen(false);
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={triggerRef as React.Ref<any>}
      tabIndex={disabled ? -1 : 0}
      {...props}
    />
  );
}
